/**
 * Die EINE Steuerregel von StoreOS. Wird von Auftrag (Steuerfall beim Import),
 * Beleg (Rechnung/Gutschrift) und Buchhaltungsexport benutzt.
 *
 * Muster und Rundungsregeln nach bbd `packages/regeln/src/steuer.ts`
 * (Herkunft: int1183_bbd, origin/main). ABWEICHUNG zu bbd: dort ist NETTO die
 * Bemessungsgrundlage (die Engine rechnet netto). Bei StoreOS kommen die
 * Betraege aus dem Shop BRUTTO (Woo: prices_include_tax, der Kunde hat genau
 * diesen Betrag bezahlt). Deshalb ist hier BRUTTO gegeben, die Steuer wird je
 * GRUPPE herausgerechnet und auf die Positionen zurueckverteilt, damit
 * Σ Positionen = Σ Gruppen exakt gilt (EN 16931 BR-CO-17 sinngemaess).
 *
 * Steuerfaelle (Ruling R-Steuer):
 *   inland    DE → Artikelsatz (19/7)
 *   eu_oss    EU-Privatkunde → Satz des ZIELLANDS je Artikelklasse (AT 20/10), Kategorie S
 *   eu_igl    EU-Geschaeftskunde mit gueltiger USt-IdNr. → 0 %, Kategorie K (v2: VIES)
 *   drittland Ausfuhr → 0 %, Kategorie G
 */
import { istEuLand, istLandcode } from './laender.js';
import type { Cents } from './geld.js';

export const STEUERFAELLE = ['inland', 'eu_oss', 'eu_igl', 'drittland'] as const;
export type Steuerfall = (typeof STEUERFAELLE)[number];

/** EN 16931 BT-118: S = Regelbesteuerung (auch OSS mit fremdem Satz), K = igL, G = Ausfuhr. */
export const STEUERKATEGORIEN = ['S', 'K', 'G'] as const;
export type Steuerkategorie = (typeof STEUERKATEGORIEN)[number];

export const STEUERKLASSEN = ['standard', 'ermaessigt'] as const;
export type Steuerklasse = (typeof STEUERKLASSEN)[number];

/**
 * USt-Saetze je EU-Land in Prozent, [standard, ermaessigt].
 * ⚠ Stand 2026-10-17, aus dem Gedaechtnis des Architekten uebernommen und NICHT
 * gegen eine amtliche Quelle geprueft. Vor dem Livegang mit dem Steuerberater
 * abgleichen (EU-Kommission „VAT rates applied in the Member States"). Der
 * ermaessigte Satz gilt fuer Buecher/Druckerzeugnisse nur, wo das Land ihn
 * dafuer vorsieht; welche N&G-Artikel „ermaessigt" sind, entscheidet die
 * Steuerklasse am Artikel, nicht diese Tabelle.
 */
export const EU_SAETZE: Record<string, readonly [number, number]> = {
  AT: [20, 10], BE: [21, 6], BG: [20, 9], CY: [19, 5], CZ: [21, 12], DE: [19, 7], DK: [25, 25],
  EE: [24, 9], ES: [21, 10], FI: [25.5, 14], FR: [20, 5.5], GR: [24, 13], HR: [25, 13], HU: [27, 5],
  IE: [23, 13.5], IT: [22, 10], LT: [21, 9], LU: [17, 8], LV: [21, 12], MT: [18, 5], NL: [21, 9],
  PL: [23, 8], PT: [23, 6], RO: [21, 11], SE: [25, 12], SI: [22, 9.5], SK: [23, 19],
};
export const EU_SAETZE_STAND = '2026-10-17 (ungeprueft, siehe Kommentar)';

export type SteuerfallErgebnis =
  | { ok: true; fall: Steuerfall; kategorie: Steuerkategorie; befreiung: Befreiung | null }
  | { ok: false; grund: 'land_unbekannt' };

export interface Befreiung {
  code: 'VATEX-EU-IC' | 'VATEX-EU-G';
  text: string;
}

export function befreiungFuer(fall: Steuerfall): Befreiung | null {
  if (fall === 'eu_igl') {
    return { code: 'VATEX-EU-IC', text: 'Steuerfreie innergemeinschaftliche Lieferung (§ 4 Nr. 1b UStG)' };
  }
  if (fall === 'drittland') {
    return { code: 'VATEX-EU-G', text: 'Steuerfreie Ausfuhrlieferung (§ 6 UStG)' };
  }
  return null;
}

/**
 * Steuerfall einer Lieferung. `ustIdGueltig` ist in v1 immer false (keine
 * VIES-Pruefung); der Fall eu_igl ist vorgesehen, aber nicht erreichbar.
 */
export function steuerfallFuer(e: { lieferland: string; ustIdGueltig?: boolean }): SteuerfallErgebnis {
  const land = e.lieferland.trim().toUpperCase();
  if (!istLandcode(land)) return { ok: false, grund: 'land_unbekannt' };
  if (land === 'DE') return { ok: true, fall: 'inland', kategorie: 'S', befreiung: null };
  if (istEuLand(land)) {
    if (e.ustIdGueltig) return { ok: true, fall: 'eu_igl', kategorie: 'K', befreiung: befreiungFuer('eu_igl') };
    return { ok: true, fall: 'eu_oss', kategorie: 'S', befreiung: null };
  }
  return { ok: true, fall: 'drittland', kategorie: 'G', befreiung: befreiungFuer('drittland') };
}

/** Der Satz einer Position in Prozent, abhaengig von Fall, Zielland und Artikelklasse. */
export function satzFuer(fall: Steuerfall, lieferland: string, klasse: Steuerklasse): number {
  if (fall === 'eu_igl' || fall === 'drittland') return 0;
  const land = fall === 'inland' ? 'DE' : lieferland.toUpperCase();
  const saetze = EU_SAETZE[land];
  if (!saetze) throw new Error(`Kein Steuersatz hinterlegt fuer ${land}`);
  return klasse === 'ermaessigt' ? saetze[1] : saetze[0];
}

export function kategorieFuer(fall: Steuerfall): Steuerkategorie {
  return fall === 'eu_igl' ? 'K' : fall === 'drittland' ? 'G' : 'S';
}

/** Eine Position, so weit es die Steuer angeht. */
export interface SteuerPosition {
  gesamt_brutto_cents: Cents;
  steuersatz: number;
  steuerkategorie: Steuerkategorie;
}

export interface Steuergruppe {
  steuersatz: number;
  kategorie: Steuerkategorie;
  brutto_cents: Cents;
  netto_cents: Cents;
  steuer_cents: Cents;
}

/** Steuer aus einem Bruttobetrag herausrechnen: steuer = brutto − round(brutto / (1 + s)). */
export function steuerAusBrutto(bruttoCents: Cents, satz: number): Cents {
  if (satz === 0) return 0;
  const netto = Math.round(bruttoCents / (1 + satz / 100));
  return bruttoCents - netto;
}

function schluessel(p: { steuersatz: number; steuerkategorie: Steuerkategorie }): string {
  return `${p.steuersatz}|${p.steuerkategorie}`;
}

/** Gruppen je (Satz, Kategorie), Steuer je Gruppe aus dem Gruppen-Brutto, absteigend nach Satz. */
export function steuergruppen(positionen: ReadonlyArray<SteuerPosition>): Steuergruppe[] {
  const map = new Map<string, Steuergruppe>();
  for (const p of positionen) {
    const k = schluessel(p);
    const g = map.get(k);
    if (g) g.brutto_cents += p.gesamt_brutto_cents;
    else map.set(k, { steuersatz: p.steuersatz, kategorie: p.steuerkategorie, brutto_cents: p.gesamt_brutto_cents, netto_cents: 0, steuer_cents: 0 });
  }
  const gruppen = [...map.values()];
  for (const g of gruppen) {
    g.steuer_cents = steuerAusBrutto(g.brutto_cents, g.steuersatz);
    g.netto_cents = g.brutto_cents - g.steuer_cents;
  }
  gruppen.sort((a, b) => b.steuersatz - a.steuersatz || a.kategorie.localeCompare(b.kategorie));
  return gruppen;
}

export function summenAusGruppen(gruppen: ReadonlyArray<Steuergruppe>): { netto_cents: Cents; steuer_cents: Cents; brutto_cents: Cents } {
  const netto_cents = gruppen.reduce((s, g) => s + g.netto_cents, 0);
  const steuer_cents = gruppen.reduce((s, g) => s + g.steuer_cents, 0);
  return { netto_cents, steuer_cents, brutto_cents: netto_cents + steuer_cents };
}

function groesster(werte: ReadonlyArray<number>, kandidaten: ReadonlyArray<number>): number {
  let best = kandidaten[0];
  for (const i of kandidaten) if (Math.abs(werte[i]) > Math.abs(werte[best])) best = i;
  return best;
}

/**
 * Gruppensteuer auf die Positionen zurueckverteilen, Rundungsrest auf die
 * betragsgroesste Position der Gruppe. Σ steuer_i = Σ gruppe.steuer EXAKT.
 * (nach bbd `steuerJePosition`, Herkunft int1183_bbd)
 */
export function steuerJePosition(positionen: ReadonlyArray<SteuerPosition>): Cents[] {
  const out = new Array<number>(positionen.length).fill(0);
  const brutto = positionen.map((p) => p.gesamt_brutto_cents);
  for (const g of steuergruppen(positionen)) {
    const idx: number[] = [];
    positionen.forEach((p, i) => { if (p.steuersatz === g.steuersatz && p.steuerkategorie === g.kategorie) idx.push(i); });
    if (idx.length === 0) continue;
    const gross = groesster(brutto, idx);
    if (g.brutto_cents === 0) { out[gross] = g.steuer_cents; continue; }
    let summe = 0;
    for (const i of idx) {
      const anteil = Math.round((g.steuer_cents * brutto[i]) / g.brutto_cents);
      out[i] = anteil;
      summe += anteil;
    }
    out[gross] += g.steuer_cents - summe;
  }
  return out;
}

/**
 * Eine Nebenleistung (Versand, Rabatt) folgt der Hauptleistung (UStAE 3.10):
 * bei Mischkoerben anteilig nach Brutto der Gruppen, Rest auf die groesste Gruppe.
 */
export function nebenleistungVerteilen(
  betragCents: Cents, gruppen: ReadonlyArray<Steuergruppe>, fallbackSatz: number,
): Array<{ steuersatz: number; steuerkategorie: Steuerkategorie; anteil_cents: Cents }> {
  if (gruppen.length === 0) return [{ steuersatz: fallbackSatz, steuerkategorie: 'S', anteil_cents: betragCents }];
  const brutto = gruppen.map((g) => g.brutto_cents);
  const basis = brutto.reduce((s, n) => s + n, 0);
  const gross = groesster(brutto, gruppen.map((_, i) => i));
  const anteile = basis === 0
    ? gruppen.map((_, i) => (i === gross ? betragCents : 0))
    : gruppen.map((g) => Math.round((betragCents * g.brutto_cents) / basis));
  if (basis !== 0) anteile[gross] += betragCents - anteile.reduce((s, a) => s + a, 0);
  return gruppen.map((g, i) => ({ steuersatz: g.steuersatz, steuerkategorie: g.kategorie, anteil_cents: anteile[i] }));
}
